import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Gradients } from '@/shared/components/theme/gradient';
import { cn } from '@/shared/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-muted-foreground underline-offset-4 hover:underline',

        /// Syncara buttons
        outline: 'border border-white/50 bg-transparent shadow-sm',
        greenGradient: `!w-full text-black bg-gradient-to-r ${Gradients.greenGradient} hover:bg-custom-green-glow active:bg-extra-light-green focus:bg-extra-light-green`,
        transparent: '',
      },
      btnType: {
        primary:
          'h-[50px] px-4 py-3 text-xs leading-[20px] font-[590] rounded-sm',
        secondary: 'h-[36px] p-2 text-2xs rounded-xs',
        icon: 'size-9',
      },
      shape: {
        default: '',
        circle: 'rounded-full aspect-square size-fit [&_svg]:size-fit',
        square: 'aspect-square',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const gradientBorderVariants = cva('bg-gradient-to-r p-px', {
  variants: {
    variant: {
      default: 'from-white to-white/30',
      whiteToHalfWhite: 'from-white to-white/50',
      green: 'from-white to-light-green/85',
    },
    btnType: {
      primary: '!h-[50px]  rounded-sm',
      secondary: '!h-[36px] rounded-xs',
      icon: 'size-9',
    },
  },
  defaultVariants: {
    variant: 'default',
    btnType: 'primary',
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  gradientBorder?: VariantProps<typeof gradientBorderVariants>['variant'];
  doubleBorderConfig?: {
    outerBorderColor?: string;
    innerBorderColor?: string;
    padding?: string;
    outerRadius?: string;
  };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      shape,
      btnType = 'primary',
      asChild = false,
      gradientBorder,
      doubleBorderConfig,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const variantsWithGradientBorder = ['greenGradient'];
    const useGradientBorder =
      gradientBorder || variantsWithGradientBorder.includes(variant ?? '');

    if (useGradientBorder) {
      const gradientVariant =
        variant === 'greenGradient' ? 'green' : gradientBorder;

      // Handle the double-layer border effect
      if (doubleBorderConfig) {
        const outerRadiusClass =
          doubleBorderConfig.outerRadius || shape === 'circle'
            ? 'rounded-full'
            : 'rounded-sm';

        return (
          <Comp
            ref={ref}
            className={cn(
              'inline-flex items-center justify-center border bg-transparent p-1',
              doubleBorderConfig.outerBorderColor || 'border-white/30',
              outerRadiusClass,
              shape === 'circle' ? 'aspect-square' : '',
              className,
            )}
            {...props}
          >
            <div
              className={cn(
                'inline-flex items-center justify-center bg-transparent',
                doubleBorderConfig.padding || 'p-2',
                outerRadiusClass,
                shape === 'circle' ? 'aspect-square' : 'w-full',
              )}
            >
              <span
                className={cn(
                  buttonVariants({
                    variant: variant,
                    shape,
                    btnType,
                  }),
                  '!h-auto !px-3 !py-2',
                  shape === 'circle' ? 'aspect-square !p-1' : 'w-full',
                )}
              >
                {children}
              </span>
            </div>
          </Comp>
        );
      }

      const innerRadiusClass =
        btnType === 'secondary'
          ? 'rounded-[calc(8px-1px)]'
          : 'rounded-[calc(12px-1px)]';

      return (
        <Comp
          ref={ref}
          className={cn(
            gradientBorderVariants({
              variant: gradientVariant,
              btnType,
            }),
            className,
          )}
          {...props}
        >
          <span
            className={cn(
              buttonVariants({ variant, className, shape, btnType }),
              innerRadiusClass,
              'h-full',
            )}
          >
            {children}
          </span>
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, className, shape, btnType }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
